import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Wallet, Users, Clock, TrendingUp, FileSpreadsheet, AlertTriangle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import KpiCard from '../../components/KpiCard';
import SectionCard from '../../components/SectionCard';
import { ChartBox, COLORS, EmptyChart } from '../registros/chartParts';
import { fetchReporteNominaCentro } from '../../services/reportesV2.services';
import { sumaGrupo } from '@/pages/personal/nomina/utils/nomina';
import { exportarReporteNominaCentro } from './nominaCentroExcel';
import InfoTip from '../../components/InfoTip';
import {
  explicaDevengado,
  explicaNeto,
  explicaCostoEmpresa,
  explicaPorHora,
  explicaRecargosExtras,
  explicaRepartoPlantas,
} from './explicaciones';

const pesos = (v) =>
  Number(v || 0).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const millones = (v) => `$${(Number(v || 0) / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
const horas = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 });

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const etiquetaQuincena = (q) => {
  const [, m, d1] = q.desde.split('-').map(Number);
  const d2 = Number(q.hasta.slice(8, 10));
  return `${d1}–${d2} ${MESES[m - 1]}`;
};

/**
 * Reporte "Nómina del centro": calcula la nómina del centro en el rango con el mismo motor
 * de Personal → Nóminas por periodo (por quincenas, sin guardar nada).
 */
const NominaCentroContent = ({ idLavanderia, startDate, endDate, laundry }) => {
  const { toast } = useToast();
  // Las fechas llegan como ISO de la medianoche local: se toma el día local.
  const desde = new Date(startDate).toLocaleDateString('en-CA');
  const hasta = new Date(endDate).toLocaleDateString('en-CA');
  const [estado, setEstado] = useState({ loading: true, error: null, data: null });
  const [texto, setTexto] = useState('');

  const cargar = useCallback(async () => {
    setEstado((e) => ({ ...e, loading: true, error: null }));
    try {
      const data = await fetchReporteNominaCentro({ idLavanderia, desde, hasta });
      setEstado({ loading: false, error: null, data });
    } catch (err) {
      setEstado({ loading: false, error: err.message, data: null });
    }
  }, [idLavanderia, desde, hasta]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const d = estado.data;
  const t = d?.totales;
  const personas = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return (d?.personas || []).filter(
      (p) => !q || `${p.empleado?.nombre} ${p.empleado?.cedula || ''} ${p.empleado?.rol || ''}`.toLowerCase().includes(q),
    );
  }, [d, texto]);

  const datosGrafica = useMemo(
    () =>
      (d?.quincenas || []).map((q) => ({
        periodo: etiquetaQuincena(q),
        Básico: Math.round(q.valores?.basico || 0),
        'Aux. transporte': Math.round(q.valores?.auxilioTransporte || 0),
        Recargos: Math.round(q.valores?.recargos || 0),
        Extras: Math.round(q.valores?.extras || 0),
      })),
    [d],
  );

  const exportar = () =>
    exportarReporteNominaCentro(d, { centro: laundry?.nombre || 'Centro' }).catch((err) =>
      toast({ title: 'No se exportó', description: err.message, variant: 'destructive' }),
    );

  if (estado.loading && !d) {
    return (
      <SectionCard title="Nómina del centro" loading>
        {null}
      </SectionCard>
    );
  }
  if (estado.error) {
    return <SectionCard title="Nómina del centro" error={estado.error} onReload={cargar} />;
  }
  if (!d) return null;

  const horasRec = sumaGrupo(t.horas, 'Recargos');
  const horasExt = sumaGrupo(t.horas, 'Extras');
  const costoHora = t.horas.trabajadas ? t.devengado / t.horas.trabajadas : 0;
  const conAvisos = d.personas.filter((p) => p.advertencias.length).length;
  const compartidas = d.personas.filter((p) => p.participacion < 1).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={<Wallet className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Costo de nómina (devengado)"
          info={
            <>
              {explicaDevengado('Solo la parte de cada persona que corresponde a sus horas en este centro.')}
              <div className="border-t my-2" />
              {explicaCostoEmpresa(d.parametrosCostoEmpresa, {
                devengado: t.devengado,
                aportes: t.costoEmpresa?.aportes?.total,
                prestaciones: t.costoEmpresa?.prestaciones?.total,
                costoEmpresa: t.costoEmpresa?.total,
              })}
            </>
          }
          value={millones(t.devengado)}
          sub={{ label: 'Neto a pagar', value: pesos(t.neto) }}
          sub2={{ label: 'Deducciones', value: pesos(t.deducciones) }}
          extra={
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-[11.5px] text-[#43474F] dark:text-gray-400">Costo total empresa</span>
              <b className="text-[13.5px] font-semibold text-[#0F3460] dark:text-gray-200">{pesos(t.costoEmpresa?.total)}</b>
            </div>
          }
        />
        <KpiCard
          icon={<Users className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Personas"
          info={explicaRepartoPlantas}
          value={t.personas}
          sub={{ label: 'Quincenas en el rango', value: d.quincenas.length }}
          sub2={{ label: 'Compartidas con otras sedes', value: compartidas }}
        />
        <KpiCard
          icon={<Clock className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Horas trabajadas"
          info={explicaPorHora}
          value={horas(t.horas.trabajadas)}
          unit="h"
          sub={{ label: 'Costo por hora trabajada', value: pesos(costoHora) }}
          sub2={{ label: 'Ausencias (h)', value: horas(sumaGrupo(t.horas, 'Ausencias')) }}
        />
        <KpiCard
          icon={<TrendingUp className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Recargos + extras"
          info={explicaRecargosExtras}
          value={millones(t.valores.recargos + t.valores.extras)}
          sub={{ label: `Recargos · ${horas(horasRec)} h`, value: pesos(t.valores.recargos) }}
          sub2={{ label: `Extras · ${horas(horasExt)} h`, value: pesos(t.valores.extras) }}
        />
      </div>

      <ChartBox title="Costo por quincena" hint="Básico, auxilio de transporte, recargos y extras de cada quincena del rango." info={explicaDevengado()}>
        {datosGrafica.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datosGrafica} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                domain={[0, (max) => Math.ceil((max * 1.1) / 1e6) * 1e6]}
                tickFormatter={(v) => `${(v / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`}
                width={52}
              />
              <Tooltip formatter={(v) => pesos(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Básico" stackId="a" fill={COLORS.navy} />
              <Bar dataKey="Aux. transporte" stackId="a" fill={COLORS.lightBlue} />
              <Bar dataKey="Recargos" stackId="a" fill={COLORS.amber} />
              <Bar dataKey="Extras" stackId="a" fill={COLORS.coral} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart />
        )}
      </ChartBox>

      <SectionCard
        title="Por quincena"
        hint={`Si la quincena tiene nómina generada se usan sus valores, igual que el análisis de nómina; si no, se calcula con los datos actuales (horas base = (días − domingos) × ${d.jornadaDiaria} h; las quincenas incompletas se prorratean).`}
        onReload={cargar}
        loading={estado.loading}
        action={
          <Button size="sm" variant="outline" onClick={exportar}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <th className="py-2 pr-3">Quincena</th>
                <th className="py-2 px-2 text-right">Base</th>
                <th className="py-2 px-2 text-right">Personas</th>
                <th className="py-2 px-2 text-right">Horas</th>
                <th className="py-2 px-2 text-right">Básico</th>
                <th className="py-2 px-2 text-right">Aux. transp.</th>
                <th className="py-2 px-2 text-right">Recargos</th>
                <th className="py-2 px-2 text-right">Extras</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Devengado<InfoTip>{explicaDevengado()}</InfoTip></span></th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Neto<InfoTip>{explicaNeto}</InfoTip></span></th>
                <th className="py-2 pl-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Costo empresa<InfoTip>{explicaCostoEmpresa(d.parametrosCostoEmpresa)}</InfoTip></span></th>
              </tr>
            </thead>
            <tbody>
              {d.quincenas.map((q) => (
                <tr key={q.desde} className="border-b last:border-0">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {etiquetaQuincena(q)}
                    {!q.completa && <span className="ml-1.5 text-[10px] text-amber-600">parcial</span>}
                    <div
                      className={`text-[10px] ${q.fuente?.desactualizada ? 'text-rose-600' : 'text-gray-500'}`}
                      title={
                        q.fuente?.desactualizada
                          ? `Hay fichas con un salario distinto al usado en el último cálculo:\n${q.fuente.desactualizada.ejemplos.join('\n')}`
                          : undefined
                      }
                    >
                      {q.fuente?.tipo === 'nomina'
                        ? `según ${q.fuente.nombre} (${q.fuente.estado})${q.fuente.desactualizada ? ' · desactualizada' : ''}`
                        : 'calculado con datos actuales (sin nómina)'}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">{q.horasBase} h</td>
                  <td className="py-2 px-2 text-right tabular-nums">{q.personas}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{horas(q.horas?.trabajadas)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(q.valores?.basico)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(q.valores?.auxilioTransporte)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(q.valores?.recargos)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(q.valores?.extras)}</td>
                  <td className="py-2 px-2 text-right tabular-nums font-medium">{pesos(q.devengado)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(q.neto)}</td>
                  <td className="py-2 pl-2 text-right tabular-nums">{pesos(q.costoEmpresa?.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title="Por persona"
        hint={`Suma de las quincenas del rango, ordenada por costo. La nómina de cada persona se calcula completa y al centro se le asigna la parte de sus horas trabajadas aquí (participación).${conAvisos ? ` ${conAvisos} con advertencias.` : ''}`}
        action={
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar" className="h-9 pl-7 w-48" />
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <th className="py-2 pr-3">Persona</th>
                <th className="py-2 px-2 text-right">Salario</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Particip.<InfoTip>{explicaRepartoPlantas}</InfoTip></span></th>
                <th className="py-2 px-2 text-right">Días</th>
                <th className="py-2 px-2 text-right">Horas</th>
                <th className="py-2 px-2 text-right">Recargos</th>
                <th className="py-2 px-2 text-right">Extras</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Devengado<InfoTip>{explicaDevengado()}</InfoTip></span></th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Neto<InfoTip>{explicaNeto}</InfoTip></span></th>
                <th className="py-2 pl-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Costo empresa<InfoTip>{explicaCostoEmpresa(d.parametrosCostoEmpresa)}</InfoTip></span></th>
              </tr>
            </thead>
            <tbody>
              {personas.map((p) => {
                const avisos = p.advertencias;
                return (
                  <tr key={p.clave} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium flex items-center gap-1 whitespace-nowrap">
                        {p.empleado?.nombre}
                        {avisos.length > 0 && (
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0">
                            <title>{avisos.join('\n')}</title>
                          </AlertTriangle>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {p.empleado?.cedula || 'Sin cédula'}
                        {p.empleado?.rol ? ` · ${p.empleado.rol}` : ''}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{pesos(p.salario)}</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${p.participacion < 1 ? 'text-amber-600' : ''}`}>
                      {Math.round((p.participacion || 0) * 100)} %
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{horas(p.diasLaborados)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{horas(p.horas.trabajadas)}</td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">
                      {pesos(p.valores.recargos)}
                      <div className="text-xs text-gray-500">{horas(sumaGrupo(p.horas, 'Recargos'))} h</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">
                      {pesos(p.valores.extras)}
                      <div className="text-xs text-gray-500">{horas(sumaGrupo(p.horas, 'Extras'))} h</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-medium">{pesos(p.devengado)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{pesos(p.neto)}</td>
                    <td className="py-2 pl-2 text-right tabular-nums">{pesos(p.costoEmpresa?.total)}</td>
                  </tr>
                );
              })}
              {personas.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-6 text-center text-gray-500">
                    Sin personas en el rango.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

export default NominaCentroContent;
